import Button from '@dustin-web/microsite-components/src/button'
import {
  useSiteContext,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  EmailStatus,
  GetEmailStatus,
  GetEmailStatusQuery,
  GetEmailStatusQueryVariables,
} from 'src/graphql/generated/graphql-types'
import { StandardRegisterProps } from '.'
import ValidationMessage from '../../validationMessage'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import Benefits from './Benefits'
import Login from './Login'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import AppContext from 'src/appContext'
import classNames from 'classnames'
import { GaRegEvent } from 'src/components/gaRegistrationHook/gaRegistrationEventHook'

export enum State {
  edit,
  runningQuery,
  isFederated,
  emailExists,
  error,
}

type Props = {
  onNavigateNext: () => void
}

const EmailStep = ({ onNavigateNext }: Props) => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<StandardRegisterProps>()
  const { translate } = useTranslations()
  const { siteType } = useSiteContext()
  const { signIn } = useAuth()
  const { twoLetterISOLanguageName } = useUserSettings()
  const { returnUrl } = useContext(AppContext)

  const [state, setState] = useState<State>(State.edit)
  const watchEmail = watch('email')

  const { emailGaEvent } = GaRegEvent()

  useGraphql<GetEmailStatusQuery, GetEmailStatusQueryVariables>(GetEmailStatus, {
    context: {
      skip: state !== State.runningQuery,
    },
    variables: {
      email: getValues('email'),
    },
    swrOptions: {
      revalidateIfStale: true,
      onSuccess: data => {
        if (data) {
          const emailStatus: EmailStatus = data?.emailStatus

          switch (emailStatus) {
            case EmailStatus.Valid:
              onNavigateNext()
              break
            case EmailStatus.IsFederated:
              setState(State.isFederated)
              break
            case EmailStatus.EmailExists:
              setState(State.emailExists)
              break
            default:
              setState(State.error)
              break
          }
        } else {
          setState(State.error)
        }
      },
    },
  })

  const handleOnClick = () => {
    emailGaEvent()
    setState(State.runningQuery)
  }

  const handleOnLoginClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    signIn({
      cancellationUrl: window.location.origin,
      provider: 'okta',
      siteType: siteType === 'Business' ? 'business' : 'consumer',
      username: watchEmail,
      returnUrl: returnUrl ?? window.location.origin,
      language: twoLetterISOLanguageName,
    })
  }

  return (
    <>
      {state === State.runningQuery && <Spinner dustinSpinner />}

      <div className="row">
        <div className="col-12 col-lg-6">
          <div className="row">
            <div className="col-12 col-lg-10">
              <div
                className={classNames('c-form__item c-form__item--mandatory', {
                  'c-form__error': errors.email,
                })}>
                <label htmlFor="register-email">{translate('Register_email')}</label>
                <input
                  id="register-email"
                  autoComplete="off"
                  type="text"
                  {...register('email')}
                  onChange={e => {
                    setValue('email', e.target.value)
                    clearErrors('email')
                    setState(State.edit)
                  }}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'emailError' : undefined}
                />
                {errors?.email && (
                  <ValidationMessage id="emailError">
                    {translate('Validation_EmailIsRequired')}
                  </ValidationMessage>
                )}
                <Login state={state} />
              </div>
            </div>
          </div>

          <div className="row">
            <div
              className={classNames('col-12 col-lg-auto', {
                'u-pr-lg-4': [State.emailExists, State.isFederated].includes(state),
              })}>
              <Button
                id="btn-email-proceed"
                icon="arrow-right"
                primary
                className="u-w-100 u-w-lg-auto"
                disabled={state !== State.edit}
                text={translate('RegisterBuinsess_Procced')}
                onClick={handleSubmit(handleOnClick)}
              />
            </div>
            {[State.emailExists, State.isFederated].includes(state) && (
              <div className="col-12 col-lg-auto u-pl-lg-4">
                <Button
                  icon="log-in"
                  className="u-w-100 u-w-lg-auto"
                  text={translate('RegisterBuinsess_Login')}
                  onClick={handleOnLoginClick}
                />
              </div>
            )}
          </div>

          {state === State.error && (
            <div className="row">
              <div className="col-12">
                <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
              </div>
            </div>
          )}
        </div>
        <div className="col-12 col-lg-6 order-lg-last u-pt-40 u-pt-lg-0">
          <Benefits />
        </div>
      </div>
    </>
  )
}

export default EmailStep
